import React from "react";
import {
  Paper,
  Title,
  Text,
  Divider,
  Stack,
  Image,
  Space,
  Group,
} from "@mantine/core";

const blogImages = require.context(
  "/public/images/writing/blogs/20240601",
  true
);

function Blog20240601() {
  return (
    <Paper
      p="xl"
      radius="lg"
      withBorder
      shadow="sm"
      mt="lg"
      mx="auto"
      maw={700}
    >
      <Stack gap="md">
        <Title order={1}>6/1/2024: Cycle times</Title>
        <Divider />
        <Title order={2} size="h3">
          It's 10:56 PM.
        </Title>
        <Text>
          &nbsp;&nbsp;&nbsp;&nbsp;I'm tired, or maybe I just think I am. It's
          kind of weird. I wake up, go to the gym, work, eat, and then sleep. I
          occasionally visit friends, but many of them live in Seattle and I'm
          waiting for the light rail station in Lynnwood to open up. Even then,
          it's still faster to drive in many cases, but at least there would be
          more options. Or they live in Bellevue and I need to find the
          motivation to drive down there. Or they live in other parts of the
          country, or they live in other countries.
        </Text>
        <Text>
          I find it hard to travel far and go places, but I should probably just
          bite the bullet and go. I don't want to stay inside all day and rot
          away, grinding away at work on one project to the next. It's hard to
          find a good balance during the work week because the entire idea of
          the 9-to-5 is that you are working with maybe 30 minutes to an hour of
          lunch in between. Then the rest of the time you're supposed to be
          working, be available to talk, join meetings, or monitor things in
          case something breaks. But the 9-to-5 was started in the 1920s and we
          just kept it alive for all this time. We are arguably the most
          productive we have ever been in human history, but we hold onto the
          antiquated idea of working those hours because that's what we did in
          the past.
        </Text>
        <Text>
          I think part of my problem is that I can just sit there and continue
          working, like there's always a way to make progress on something, so I
          can just keep going. There's always something to fix or something to
          improve. Another part of the problem is that I'm aware of the rotting.
          When I do things, I cycle through hyperfixating on things, then
          getting tired, then bored? Then I get burnt out if I work too much and
          question why I'm doing anything. At that point, I consider if I should
          just close up shop and go on a journey of self-discovery. Although I
          almost never do, or when I do, it is only to the extent that questions
          get asked about my relationship with work.
        </Text>
        <Text>
          How did the generations before us do it? Maybe they had brighter
          futures, higher hopes? Did they have a higher sense of duty to the
          work that they do? To the company they spend their limited time on
          earth grinding away for? To the family that they're working to
          support? Maybe it's the reinforcement of societal norms. Or it could
          be the idea that previous generations had it worse, therefore we
          should be grateful for what we have and how we have it? Maybe
          inflation wasn't as bad back then? Maybe they could own and buy a
          house and raise a family? Maybe life was not as bleak?
        </Text>
        <Title order={2} size="h3" ta="center">
          Where did the collective shine in our eyes go?
        </Title>
        <Text>
          Where is the purpose that we are all searching for? Does it actually
          exist or is it something we made up to cope with the heavy, crushing
          reality we find ourselves living in (read: struggling through)? I know
          that I am pessimistic by nature, but this is not purely a personal
          struggle. This is not purely an American struggle, even. Young people
          everywhere feel unprecedented hopelessness. I don't think it's that
          they lack purpose, but that they lack the ability to see themselves in
          the future being happy and content with their lives.
        </Text>
        <Text>
          I think I'm okay at what I do, or at least that's what people tell me.
          Although I think I'm starting to let it get to my head and it's making
          me more rash, dismissive, and overconfident in how I'm doing things. I
          might be complaining too much, but is the intensity of the suffering I
          feel overexaggerated? I know that I am in a privileged situation in
          that I can work from home and make money, but this is how I feel and
          is it wrong of me to feel that way? I guess everyone's perspectives
          are different and they experience life at their own pace. I think I'm
          in my own head too often.
        </Text>
        <iframe
          title="How it feels to work"
          style={{ borderRadius: "12px" }}
          src="https://open.spotify.com/embed/track/6D0ihmDU6CQoenaeFtKjkj?utm_source=generator"
          width="100%"
          height="236"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
        <Text>
          Maybe that's just how life is. It feels like I'm cycling between
          feeling alright, that I'm making progress in the life that I live, and
          feeling general angst; like I'm not doing enough or not where I should
          be. I might be burnt out, maybe from work, or maybe the monotony of
          the life that I live. I think there's something wrong with me, but I
          could also be imagining problems that don't exist. Although to deny
          one's own perception of the world is also a sort of oppression of the
          self.
        </Text>
        <Text>
          When people look at your life from the outside, how would they
          perceive it? Not that it should matter, but what would they think?
          What would they think about the way that you live your life? Your
          schedule, your diet, your habits, your quirks, what you stand for and
          believe in. In reality, it doesn't matter what other people think of
          you and what actually matters is what you think of yourself. While it
          is incredibly reductive; you are, in part, what you do. This overlooks
          the dreams and aspirations that people have, but what strides are you
          making towards achieving your goals? If you want to learn a new
          language, what are you doing to make that happen? Although, in many
          cases, it is usually only those that are privileged enough to be able
          to choose what they do. There is usually some level of effort
          involved; but to be able to choose what you can do, to be the master
          of your own destiny, is not a privilege that most people have or can
          afford.
        </Text>
        <Title order={2} size="h3" ta="center">
          In life, we are often pulled in different directions than what we
          originally set out for.
        </Title>
        <Text>
          I guess what I want to say is that if you're trying to be the best
          version of yourself, what are you doing to get there? How do you get
          from point A to B and not fall into an endless cycle of dread,
          boredom, inactivity, and despair? What are we doing with our lives
          such that we are improving and making strides towards what we want to
          do and where we want to be?
        </Text>
        <iframe
          title="Hopeful?"
          style={{ borderRadius: "12px" }}
          src="https://open.spotify.com/embed/track/3tnfHy341CDlAtcSmUtuOG?utm_source=generator"
          width="100%"
          height="236"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
        <Text>
          &nbsp;&nbsp;&nbsp;&nbsp;I think there's something wrong with me. I get
          overwhelmed. I find it hard to start things because if I start
          something, I would like to finish it, otherwise I feel like I failed
          somehow. Like I lost something, or that I'm a loser. This is a
          negative attitude that should probably be addressed, since in the
          marathon of life, taking the first step is crucial to getting anything
          done. You might not know how to do something, but you can start to
          learn by taking the plunge, the first step, or whatever distance
          metaphor people generally use for starting something. Sometimes we
          convince ourselves that it is difficult to do, so we don't even start.
          Other times, we tell ourselves that it is easy to do, only to learn
          that we have grossly underestimated the scope of whatever we're trying
          to achieve. We tell ourselves that the limited time we have is better
          spent one way or another.
        </Text>
        <Title order={2} size="h3" ta="center">
          Or that we're just waiting for X or Y before starting something.
          Surely, it was the lack of X or Y that prevented us from achieving
          something in the past. After X or Y, we will have more time, more
          energy, more gusto.
        </Title>
        <Text>
          This is likely why things like new years resolutions often fail. We
          predicate starting on an arbitrary milestone that will just pass, like
          any other day of the year. We tell ourselves that it will be different
          this time around, that we're cracked the code on how our brains tick.
          That this time, we will succeed in what we have failed to do countless
          times before.
        </Text>
        <Text>
          We should just do it; bite that bullet, pull that trigger, let it rip.
          We might fail. Actually, in all likelihood, we will probably fail, but
          maybe that's just part of the journey. To be resilient is to try and
          try again against whatever odds, however great the probability of
          failure is. Whatever badness the outcome of failure carries with it.
          We will not get anything done by sitting around and twiddling our
          thumbs, waiting for the new year or some vacation or break to do
          something. We kind of just have to take life by the reins and enjoy
          the ride. It's easy to say that we can't do something when we haven't
          even tried. It's harder to say that we can't do something after
          actually trying, since problems at that point are generally
          identifiable and addressable. Nothing ventured, nothing gained.
        </Text>
        <Text>
          Successful people have failed in the past, they have pivoted ideas,
          they have uprooted their sense of how the world works. They constantly
          question themselves and their reality to make it make sense. Sometimes
          this works, other times people may fall into a trap and believe
          themselves to be right(eous). They consider the world, then they
          accept their own interpretation of reality, in a sort of grand and
          convoluted circumvention of reality (I daresay, rejection of reality
          but we will revist this at a different time...).
        </Text>
        <Text>
          Failing is a part of the process. Maybe you give up after a day. Maybe
          you do it after a month? The day or month you put in and stopped still
          brings you that much closer to whatever you want to do. Maybe it's
          messy, unprincipled, raw, unfettered? Who cares? It can still be
          considered progress.
        </Text>
        <Title order={2} size="h3" ta="center">
          Who we are as a person generally isn't defined by a single day, but by
          the life we have lived. With our limited time, every day matters and
          any progress is infinitely better than no progress. To take a step is
          generally much better than standing still.
        </Title>
        <Text>
          In any case, I might be burnt out. I might be depressed. I might have
          crippling social anxiety and I very likely have neurodivergent
          tendencies. I don't think I'm normal. I mean, in this day and age, who
          is? Maybe my frame of reference is messed up. Everyone considers
          themselves normal until they learn that they aren't. If it makes sense
          to you, it might not make sense to the vast majority of people, at
          least not in the same way it does to you. We kind of just go on with
          our lives in a way assuming that people think the same way as we do
          and we become shocked when we learn that this isn't the case. It's
          kind of hard to put it into words. A word for not being able to be
          described is indescribable, but this isn't exactly that. Since while
          it is hard to describe, it is not impossible to describe.
        </Text>
        <Text>I guess it's just a feeling I have.</Text>
        <Text>
          Maybe this is just the life that I live. In the universe of possible
          outcomes, maybe there is nothing wrong with me. But then why does it
          feel like there is? It's not my goal in life to stand out and be
          different and I don't think I'm crazy. Maybe I'm just aware of the
          differences. In life, a lot of things won't matter to anyone else but
          you. I guess what I'm trying to say is that people may be
          self-conscious, but they should know that they generally pay more
          attention to themselves than anyone else would pay attention to them.
          So if you get embarassed, just know that it'll stick with you for a
          long time, but in the universal consciousness, it is just a blip in
          its infinite awareness. And Time goes on.
        </Text>
        <iframe
          title="It do be wild"
          style={{ borderRadius: "12px" }}
          src="https://open.spotify.com/embed/track/0MebeWX9y2irXrCCNcK58i?utm_source=generator&theme=0"
          width="100%"
          height="236"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
        <Text>
          Surviving is difficult, thriving is nearly impossible. Some people may
          argue that this just isn't true, but everyone has their own set of
          standards. What is okay to someone might be great to someone else. I
          think it's important to respect how other people judge things or
          assign value. Since you can usually learn something from the decisions
          and reasoning or you learn more about how the person sees the world.
        </Text>
        <Text>
          Personally, I think most things are okay. Generally okay means okay
          with slight +/- deviations and I usually respond with that when people
          ask me how I'm doing. Although what I have been told is that it's
          usually better to respond with "good", which is weird to me because at
          some point good becomes normal unless something exceptional happens?
          It's also kind of fake to always say that everything is good. Like we
          have different things going on in our lives and not everything will
          always be good or great.
        </Text>
        <Text>
          I think people have mentioned before that having a positive leaning
          helps you feel more happy. Or that you just perceive life in a
          brighter, lighter way. I'm not sure how true this is because it seems
          like it's just cope. Although I have also heard that smiling more
          makes you more happy. Maybe in a way we've kind of just Pavlov'ed
          ourselves into thinking that. We smile when we are happy, so by
          smiling maybe we'd be a little happier.
        </Text>
        <Text>
          What may be a small inconvenience may color the rest of your day with
          a negative tint. I think what's important is to let go and being able
          to start again. Like the day is not ruined because you accidentally
          dropped your keys or something didn't go your way. I think I have a
          problem with this as well, since I linger, I ruminate, I ponder on the
          things that happen in my life. How the events could have played out
          differently, how it could've been better. Instead of doing that, I
          should probably go out of my way to make it better. How do we make it
          better? Try to make it more meaningful. Be a more meaningful,
          principled person; think through your actions and have a plan. It's
          important to reflect, but do not linger for longer than you have to.
        </Text>
        <iframe
          title="Maybe it's okay"
          style={{ borderRadius: "12px" }}
          src="https://open.spotify.com/embed/track/333eJWCVb1L7uYVu65sZx3?utm_source=generator&theme=0"
          width="100%"
          height="236"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
        <Title order={2} size="h3" ta="center">
          Holding onto the past in the present may cause you to miss out on your
          future.
        </Title>
        <Group>
          <Image
            src={blogImages("./capybara.jpg")}
            alt="capybara"
            width="200px"
            mx="auto"
            radius="md"
            my="md"
          />
        </Group>
        <Text>
          Maybe that's just it. The key to living a happy life is to learn to
          let go and moving on, generally unbothered. Well, maybe it's not the
          key, but an important part of being happy. There is no silver bullet
          to happiness. We kind of just have to live our lives to the fullest in
          the short time we have on this earth with the people we have in our
          lives.
        </Text>
        <Title order={2} size="h3" ta="center">
          One day at a time.
        </Title>
      </Stack>
    </Paper>
  );
}

export default Blog20240601;
